import React, { useEffect, useState } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  HStack,
  useToast,
  useColorModeValue,
  Switch,
  Text,
  Badge,
  Box,
  InputGroup,
  InputRightElement,
  IconButton,
} from '@chakra-ui/react'
import { useForm } from 'react-hook-form'
import {
  useCreatePaymentGateway,
  useUpdatePaymentGateway,
  useTestGatewayConnection,
} from 'queries/paymentGateway.query'
import { FaStripe, FaPaypal, FaEye, FaEyeSlash } from 'react-icons/fa'
import { MdFlashOn } from 'react-icons/md'

export function GatewayModal({ isOpen, onClose, gateway = null }) {
  const isEditing = Boolean(gateway)
  const toast = useToast()
  const createMutation = useCreatePaymentGateway()
  const updateMutation = useUpdatePaymentGateway()
  const testMutation = useTestGatewayConnection()

  const [showSecretKey, setShowSecretKey] = useState(false)
  const [testingConnection, setTestingConnection] = useState(false)

  const textColor = useColorModeValue('gray.800', 'white')
  const bgBox = useColorModeValue('gray.50', 'navy.800')
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200')

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { isSubmitting },
  } = useForm({
    defaultValues: {
      name: '',
      provider: 'stripe',
      mode: 'sandbox',
      is_active: true,
      is_default: false,
      description: '',
      publishable_key: '',
      secret_key: '',
      webhook_secret: '',
      client_id: '',
      client_secret: '',
      webhook_id: '',
    },
  })

  const provider = watch('provider')
  const mode = watch('mode')

  useEffect(() => {
    if (gateway) {
      const masked = gateway.masked_credentials || {}
      reset({
        name: gateway.name || '',
        provider: gateway.provider || 'stripe',
        mode: gateway.mode || 'sandbox',
        is_active: gateway.is_active ?? true,
        is_default: gateway.is_default ?? false,
        description: gateway.description || '',
        publishable_key: masked.publishable_key || '',
        secret_key: masked.secret_key || '',
        webhook_secret: gateway.webhook_secret || masked.webhook_secret || '',
        client_id: masked.client_id || '',
        client_secret: masked.client_secret || '',
        webhook_id: gateway.webhook_secret || masked.webhook_id || '',
      })
    } else {
      reset({
        name: '',
        provider: 'stripe',
        mode: 'sandbox',
        is_active: true,
        is_default: false,
        description: '',
        publishable_key: '',
        secret_key: '',
        webhook_secret: '',
        client_id: '',
        client_secret: '',
        webhook_id: '',
      })
    }
    setShowSecretKey(false)
  }, [gateway, isOpen, reset])

  const handleTestConnection = async () => {
    const currentValues = watch()
    setTestingConnection(true)

    const testPayload = {
      provider: currentValues.provider,
      mode: currentValues.mode,
      credentials:
        currentValues.provider === 'stripe'
          ? {
              secret_key: currentValues.secret_key,
              publishable_key: currentValues.publishable_key,
            }
          : {
              client_id: currentValues.client_id,
              client_secret: currentValues.client_secret,
            },
    }

    try {
      const res = await testMutation.mutateAsync({
        id: gateway ? gateway.id : null,
        data: testPayload,
      })

      if (res?.success) {
        toast({
          title: 'Connection Successful',
          description: res.message || 'API credentials verified successfully.',
          status: 'success',
          duration: 4000,
          isClosable: true,
        })
      } else {
        toast({
          title: 'Connection Failed',
          description: res?.message || 'Verification failed.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
      }
    } catch (err) {
      toast({
        title: 'Connection Test Error',
        description: err.response?.data?.message || err.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      setTestingConnection(false)
    }
  }

  const onSubmit = async (values) => {
    try {
      const credentials = {}
      if (values.provider === 'stripe') {
        credentials.publishable_key = values.publishable_key?.trim()
        credentials.secret_key = values.secret_key?.trim()
        credentials.webhook_secret = values.webhook_secret?.trim()
      } else {
        credentials.client_id = values.client_id?.trim()
        credentials.client_secret = values.client_secret?.trim()
        credentials.webhook_id = values.webhook_id?.trim()
      }

      const payload = {
        name: values.name.trim(),
        provider: values.provider,
        mode: values.mode,
        is_active: values.is_active,
        is_default: values.is_default,
        description: values.description?.trim() || null,
        webhook_secret:
          values.provider === 'stripe'
            ? values.webhook_secret?.trim()
            : values.webhook_id?.trim(),
        credentials,
      }

      if (isEditing) {
        await updateMutation.mutateAsync({ id: gateway.id, data: payload })
        toast({
          title: 'Payment Gateway Updated',
          description: `Gateway "${payload.name}" was successfully updated.`,
          status: 'success',
          duration: 3500,
        })
      } else {
        await createMutation.mutateAsync(payload)
        toast({
          title: 'Payment Gateway Created',
          description: `Gateway "${payload.name}" is now ready for use.`,
          status: 'success',
          duration: 3500,
        })
      }
      onClose()
    } catch (err) {
      toast({
        title: 'Operation Failed',
        description: err.response?.data?.message || 'An error occurred while saving.',
        status: 'error',
        duration: 5000,
      })
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent as="form" onSubmit={handleSubmit(onSubmit)} borderRadius="20px">
        <ModalHeader borderBottom="1px solid" borderColor={borderColor}>
          <HStack spacing={3}>
            {provider === 'stripe' ? (
              <Box color="#635BFF" fontSize="24px">
                <FaStripe />
              </Box>
            ) : (
              <Box color="#003087" fontSize="20px">
                <FaPaypal />
              </Box>
            )}
            <Text fontWeight="bold" fontSize="lg">
              {isEditing ? 'Edit Payment Gateway' : 'Add Payment Gateway'}
            </Text>
            <Badge
              colorScheme={mode === 'live' ? 'green' : 'orange'}
              borderRadius="full"
              px={2}
            >
              {mode.toUpperCase()}
            </Badge>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody py={6}>
          <VStack spacing={5} align="stretch">
            {/* Gateway Name & Provider */}
            <HStack spacing={4}>
              <FormControl isRequired flex={1}>
                <FormLabel fontSize="sm" fontWeight="bold">
                  Gateway Name
                </FormLabel>
                <Input
                  placeholder="e.g. Frankfurt Concert Stripe, Main PayPal"
                  {...register('name', { required: true })}
                />
              </FormControl>

              <FormControl isRequired width="160px">
                <FormLabel fontSize="sm" fontWeight="bold">
                  Provider
                </FormLabel>
                <Select {...register('provider')} isDisabled={isEditing}>
                  <option value="stripe">Stripe</option>
                  <option value="paypal">PayPal</option>
                </Select>
              </FormControl>
            </HStack>

            {/* Mode & Default Switch */}
            <Box p={4} borderRadius="14px" bg={bgBox} border="1px solid" borderColor={borderColor}>
              <HStack justify="space-between" wrap="wrap" gap={4}>
                <FormControl width="auto">
                  <FormLabel fontSize="sm" fontWeight="bold" mb={1}>
                    Environment Mode
                  </FormLabel>
                  <Select size="sm" borderRadius="8px" width="130px" {...register('mode')}>
                    <option value="sandbox">Sandbox / Test</option>
                    <option value="live">Live / Production</option>
                  </Select>
                </FormControl>

                <FormControl width="auto" display="flex" alignItems="center">
                  <FormLabel htmlFor="is_default" mb="0" fontSize="sm" fontWeight="bold" mr={3}>
                    Set as Default
                  </FormLabel>
                  <Switch id="is_default" colorScheme="brand" {...register('is_default')} />
                </FormControl>

                <FormControl width="auto" display="flex" alignItems="center">
                  <FormLabel htmlFor="is_active" mb="0" fontSize="sm" fontWeight="bold" mr={3}>
                    Active
                  </FormLabel>
                  <Switch id="is_active" colorScheme="green" {...register('is_active')} />
                </FormControl>
              </HStack>
            </Box>

            {/* Credentials Fields */}
            {provider === 'stripe' ? (
              <VStack spacing={4} align="stretch">
                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="bold">
                    Stripe Secret Key (API Key)
                  </FormLabel>
                  <InputGroup>
                    <Input
                      type={showSecretKey ? 'text' : 'password'}
                      placeholder={isEditing ? 'Leave unchanged or enter new key' : 'sk_live_... or sk_test_...'}
                      {...register('secret_key')}
                    />
                    <InputRightElement>
                      <IconButton
                        size="sm"
                        variant="ghost"
                        icon={showSecretKey ? <FaEyeSlash /> : <FaEye />}
                        onClick={() => setShowSecretKey(!showSecretKey)}
                      />
                    </InputRightElement>
                  </InputGroup>
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="bold">
                    Stripe Publishable Key
                  </FormLabel>
                  <Input
                    placeholder="pk_live_... or pk_test_..."
                    {...register('publishable_key')}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="bold">
                    Stripe Webhook Signing Secret (Optional)
                  </FormLabel>
                  <Input
                    placeholder="whsec_..."
                    {...register('webhook_secret')}
                  />
                </FormControl>
              </VStack>
            ) : (
              <VStack spacing={4} align="stretch">
                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="bold">
                    PayPal Client ID
                  </FormLabel>
                  <Input
                    placeholder="PayPal REST App Client ID"
                    {...register('client_id')}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="bold">
                    PayPal Client Secret
                  </FormLabel>
                  <InputGroup>
                    <Input
                      type={showSecretKey ? 'text' : 'password'}
                      placeholder={isEditing ? 'Leave unchanged or enter new secret' : 'PayPal REST App Secret'}
                      {...register('client_secret')}
                    />
                    <InputRightElement>
                      <IconButton
                        size="sm"
                        variant="ghost"
                        icon={showSecretKey ? <FaEyeSlash /> : <FaEye />}
                        onClick={() => setShowSecretKey(!showSecretKey)}
                      />
                    </InputRightElement>
                  </InputGroup>
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="bold">
                    PayPal Webhook ID (Optional)
                  </FormLabel>
                  <Input
                    placeholder="Webhook ID created in PayPal Developer Dashboard"
                    {...register('webhook_id')}
                  />
                </FormControl>
              </VStack>
            )}

            {/* Description */}
            <FormControl>
              <FormLabel fontSize="sm" fontWeight="bold">
                Notes / Description (Optional)
              </FormLabel>
              <Input
                placeholder="e.g. Account dedicated for Hamburg 2026 events"
                {...register('description')}
              />
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter borderTop="1px solid" borderColor={borderColor} justifyContent="space-between">
          <Button
            leftIcon={<MdFlashOn />}
            colorScheme="orange"
            variant="outline"
            size="sm"
            borderRadius="10px"
            isLoading={testingConnection}
            onClick={handleTestConnection}
          >
            Test Connection
          </Button>

          <HStack spacing={3}>
            <Button variant="ghost" onClick={onClose} borderRadius="10px">
              Cancel
            </Button>
            <Button
              colorScheme="brand"
              type="submit"
              isLoading={isSubmitting || createMutation.isLoading || updateMutation.isLoading}
              borderRadius="10px"
            >
              {isEditing ? 'Save Changes' : 'Create Gateway'}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
